import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { APP_STORE_URL } from '../../pages/Home';
import AppStoreButton from './AppStoreButton';
import AutoVideo from './AutoVideo';

/**
 * Entrance is pure CSS (keyframes in Home.css, staggered with --i) so it
 * finishes even in a background tab or a prerender, where JS animation
 * frames are throttled. Motion only drives the scroll-linked drift of the
 * copy. The bird never moves or scales (design/DESIGN.md): it fades in and
 * its speech bubble pops beside it.
 */
export default function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const copyY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -40]);

  const words = ['Be', 'the', 'calm', 'one', 'in', 'the', 'room.'];

  return (
    <header ref={ref} className="hero" aria-label="Better Parent Everyday">
      <motion.div className="hero__copy" style={{ y: copyY }}>
        <p className="eyebrow rise-in" style={{ '--i': 0 } as React.CSSProperties}>
          Calm parenting, three minutes a day
        </p>

        <h1 className="hero__title">
          {words.map((w, i) => (
            <span className="word-clip" key={w + i}>
              <span className="word" style={{ '--i': i + 1 } as React.CSSProperties}>{w}</span>
            </span>
          ))}
        </h1>

        <p className="hero__lead rise-in" style={{ '--i': 8 } as React.CSSProperties}>
          Ask what to do tonight. Practice the meltdown before it happens.
          Learn the skill in three-minute lessons. One small step a day, free on iPhone and iPad.
        </p>

        <div className="hero__actions rise-in" style={{ '--i': 10 } as React.CSSProperties}>
          <AppStoreButton href={APP_STORE_URL} />
          <a className="btn btn--ghost" href="#how">
            See how it works
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12l7 7 7-7" />
            </svg>
          </a>
        </div>
      </motion.div>

      <div className="hero__stage">
        <div className="hero__sky wipe-in" aria-hidden="true" />
        <div className="hero__video rise-in" style={{ '--i': 4 } as React.CSSProperties}>
          <AutoVideo
            src="/videos/iphone-preview.mp4"
            poster="/videos/iphone-preview.jpg"
            width={600}
            height={1300}
            label="A 28-second tour of the app: Home, a Learn lesson, Solve, the Tantrum Trainer, widgets and book summaries"
            eager
          />
        </div>
        <div className="hero__bird">
          <p className="bubble bubble--pop" style={{ '--i': 12 } as React.CSSProperties}>
            Two slow breaths. Then one calm line.
          </p>
          <img
            className="fade-in"
            style={{ '--i': 10 } as React.CSSProperties}
            src="/images/family/dad_wave.webp"
            width={374}
            height={426}
            alt=""
            decoding="async"
          />
        </div>
      </div>
    </header>
  );
}
